import { phoneNumberNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Form, Input, Modal } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserRequestPhoneUpdate, useUserUpdatePhone } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { MOBILE_NUMBER_COUNTRY_WHITELIST, ModalSizes } from "../../../../common/constants";
import { AgentUserProfile, ClientUserProfile, RequestUserPhoneUpdate, UpdateUserPhone } from "../../types";

interface Props {
  open: boolean;
  user: AgentUserProfile | ClientUserProfile;
  onFormCancel: () => void;
}

type FormState = "request-form" | "update-form";

export const PhoneUpdateForm = ({ open, user, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [formState, setFormState] = useState<FormState>("request-form");

  const [form] = Form.useForm<RequestUserPhoneUpdate | UpdateUserPhone>();

  const requestPhoneUpdate = useUserRequestPhoneUpdate();
  const updatePhone = useUserUpdatePhone();

  useEffect(() => {
    if (open && user && formState === "request-form") {
      form.setFieldsValue({
        optimisticLockVersion: user.optimisticLockVersion,
        phoneToConfirm: undefined,
        password: undefined,
      });
    }
  }, [open, user, formState, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (formState === "request-form") {
          requestPhoneUpdate.mutate(
            { id: user.id, object: values as RequestUserPhoneUpdate },
            {
              onSuccess: () => setFormState("update-form"),
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        } else {
          updatePhone.mutate(
            { id: user.id, object: values as UpdateUserPhone },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={open}
      title={t("user.titles.updatePhone")}
      okText={formState === "update-form" ? t("common.save") : t("user.actions.verifyTotpCodeUsingSms")}
      cancelText={t("common.cancel")}
      confirmLoading={requestPhoneUpdate.isPending || updatePhone.isPending}
      afterClose={() => {
        form.resetFields();
        setFormState("request-form");
      }}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="userPhoneUpdateForm">
        {formState === "request-form" && (
          <>
            <HiddenInput name="optimisticLockVersion" />
            <Form.Item
              name="phoneToConfirm"
              label={t("user.attrs.phoneToConfirm")}
              rules={[validations.notBlank, validations.mobilePhoneNumber(MOBILE_NUMBER_COUNTRY_WHITELIST)]}
              normalize={phoneNumberNormalizeFunction}
            >
              <Input />
            </Form.Item>

            <Form.Item name="password" label={t("user.attrs.previousPassword")} rules={[validations.notBlank]}>
              <Input.Password />
            </Form.Item>
          </>
        )}
        {formState === "update-form" && (
          <Form.Item
            name="smsTotpCode"
            label={t("user.attrs.smsTotpCode")}
            rules={[validations.notBlank, validations.length(6), validations.numeric]}
          >
            <Input />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};
